// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

/**
 * English UI strings for the embedded Shopify app (v1 ships English only). Components never
 * write text inline: scripts/check-strings.mjs fails on it. Keys are prefixed by area;
 * placeholders look like {name}. Copy follows Shopify's voice: short, plain, sentence case.
 */
export const en = {
  "app.name": "Granite",

  "nav.home": "Home",
  "nav.catalog": "Catalog",
  "nav.subscriptions": "Subscriptions",
  "nav.granite": "Granite",
  "nav.usage": "Usage",
  "nav.activity": "Activity",
  "nav.help": "Help",

  "page.home": "Home",
  "page.catalog": "Catalog",
  "page.catalogSettings": "Catalog settings",
  "page.subscriptions": "Subscriptions",
  "page.granite": "Granite",
  "page.usage": "Usage",
  "page.activity": "Activity",
  "page.help": "Help",
  "page.notFound": "Page not found",

  "usage.account": "Granite account",
  "usage.storage": "Storage",
  "usage.notConnected": "No Granite account is connected yet.",
  "usage.connect": "Connect on Home",
  "notFound.body": "This page doesn't exist.",
  "notFound.home": "Go to Home",

  "loading.page": "Loading",

  "error.heading": "Something went wrong",
  "error.retry": "Try again",
  "error.network": "Granite couldn't be reached. Check your connection and try again.",
  "error.session": "Your Shopify session couldn't be verified. Reload the page to sign in again.",
  "error.unexpected": "An unexpected error occurred.",

  "attention.one": "1 item needs attention",
  "attention.other": "{count} items need attention",
  "attention.view": "View activity",

  "hero.logoAlt": "Granite for Shopify",
  "hero.tagline": "Your store as Markdown notes, kept in sync. Search it, write in it, and let your AI tools answer from it.",
  "hero.notes.title": "Every product as a note",
  "hero.notes.body": "Variants, prices, stock, media, SEO, and subscription plans in notes you can search and link.",
  "hero.sync.title": "Always up to date",
  "hero.sync.body": "Changes in Shopify reach your notes within seconds. Your own notes under each product are kept.",
  "hero.ai.title": "Ask your AI about your catalog",
  "hero.ai.body": "Connect Claude, ChatGPT, or Cursor to Granite and get answers from your own store.",
  "setup.step": "Step {n}",
  "overview.granite": "Your vault",
  "overview.graniteBody": "Read, write, and ask your AI tools about your store.",
  "sync.now": "Sync now",
  "sync.running": "Syncing…",
  "sync.started": "Sync started. Your notes update in a few minutes.",
  "sync.finished": "Sync finished",
  "sync.failed": "The sync couldn't start. Try again.",
  "overview.syncing": "Syncing now…",
  "setup.heading": "Setup guide",
  "setup.intro": "Finish these steps to get your store into Granite.",
  "setup.progress": "{done} of {total} tasks complete",
  "setup.dismiss": "Dismiss setup guide",
  "setup.dismissed": "Setup guide dismissed",
  "setup.dismissFailed": "The setup guide couldn't be dismissed.",
  "setup.done": "Done",
  "setup.todo": "Not started",
  "setup.import.title": "Import your catalog",
  "setup.import.body": "Granite writes your products and store content as notes in your vault.",
  "setup.import.queued": "Import starting…",
  "setup.import.running": "Importing…",
  "setup.import.finished": "Imported",
  "setup.import.failed": "The import stopped with an error.",
  "setup.import.none": "The import starts automatically.",
  "setup.import.afterConnect": "The import starts after you connect a Granite account.",
  "setup.import.limit": "The import stopped at {limit} products.",
  "setup.open.title": "Open your store in Granite",
  "setup.open.body": "Read and edit your catalog notes on the web, desktop or phone.",
  "setup.open.action": "Open in Granite",
  "setup.account.title": "Connect your Granite account",
  "setup.account.body": "Connect your Granite account, or a workspace you administer. Your store's notes go into its vault.",
  "setup.account.action": "Connect Granite account",
  "setup.account.waiting": "Finish connecting in the new tab. This page updates by itself.",
  "setup.account.openAgain": "Open the connect page",
  "setup.account.connected": "Connected to {name}",
  "setup.account.workspace": "Connected to the {workspace} workspace ({name})",

  "overview.heading": "Overview",
  "overview.notes": "Notes synced",
  "overview.lastSync": "Last sync",
  "overview.never": "Not synced yet",
  "overview.empty": "No notes yet.",
  "overview.catalog": "View catalog",

  "list.loadMore": "Load more",
  "save.save": "Save",
  "save.discard": "Discard",
  "modal.cancel": "Cancel",

  "catalog.resync": "Re-sync",
  "catalog.resync.for": "Re-sync {title}",
  "catalog.resync.started": "Re-sync started for {title}",
  "catalog.openNote": "Open note",
  "catalog.openNote.for": "Open the note for {title} in Granite",
  "catalog.openShopify": "Open in Shopify",
  "catalog.openShopify.for": "Open {title} in Shopify",
  "catalog.filter.type": "Type",
  "catalog.filter.all": "All types",
  "catalog.filter.allCount": "All types ({count})",
  "catalog.filter.typeCount": "{type} ({count})",
  "catalog.search": "Search",
  "catalog.search.placeholder": "Search by title or handle",
  "catalog.empty": "Nothing has been imported yet.",
  "catalog.empty.search": "Nothing matches “{q}”.",
  "catalog.col.title": "Title",
  "catalog.col.type": "Type",
  "catalog.col.state": "Status",
  "catalog.col.synced": "Synced",
  "catalog.col.actions": "Actions",
  "catalog.state.archived": "Archived",
  "catalog.state.deleted": "Removed from Shopify",
  "catalog.state.deferred": "Waiting to sync",
  "catalog.state.error": "Sync error",
  "catalog.lastSync": "Last sync: {date}",
  "catalog.lastReconcile": "Last full check: {date}",
  "catalog.type.product": "Product",
  "catalog.type.collection": "Collection",
  "catalog.type.page": "Page",
  "catalog.type.article": "Blog post",
  "catalog.type.policy": "Policy",
  "catalog.type.metaobject": "Metaobject",
  "catalog.type.discount": "Discount",
  "catalog.type.menu": "Menu",
  "catalog.type.file": "File",
  "catalog.types.product": "Products",
  "catalog.types.collection": "Collections",
  "catalog.types.page": "Pages",
  "catalog.types.article": "Blog posts",
  "catalog.types.policy": "Policies",
  "catalog.types.metaobject": "Metaobjects",
  "catalog.types.discount": "Discounts",
  "catalog.types.menu": "Menus",
  "catalog.types.file": "Files",

  "settings.saved": "Settings saved",
  "settings.invalid": "Some settings couldn't be saved",
  "settings.resources.heading": "What to import",
  "settings.resources.intro": "Each switch adds that part of your store to your vault as notes. Turning one off keeps the notes already written.",
  "settings.resources.products": "Products",
  "settings.resources.collections": "Collections",
  "settings.resources.pages": "Pages",
  "settings.resources.blog": "Blog posts",
  "settings.resources.policies": "Policies",
  "settings.resources.store": "Store profile",
  "settings.resources.metaobjects": "Metaobjects",
  "settings.resources.discounts": "Discounts",
  "settings.resources.navigation": "Navigation menus",
  "settings.resources.files": "Files index",
  "settings.resources.translations": "Translations",
  "settings.folder.heading": "Folder and media",
  "settings.folder.label": "Folder name",
  "settings.folder.details": "Your store's notes are written into this folder of the vault.",
  "settings.media.label": "Download media into the vault",
  "settings.media.details": "Product images are saved as files, inside your storage limit. When off, notes link to Shopify's CDN.",
  "settings.keepRemoved.label": "Keep notes for removed items",
  "settings.keepRemoved.details": "When something is deleted in Shopify, its note stays and is marked as removed.",
  "settings.writeback.heading": "Write-back to Shopify",
  "settings.writeback.intro": "Edits to these parts of a product note are sent to Shopify.",
  "settings.writeback.title": "Product title",
  "settings.writeback.tags": "Tags",
  "settings.writeback.seo": "SEO title and description",
  "settings.writeback.description": "Description",
  "settings.kb.heading": "Knowledge base",
  "settings.kb.label": "Use the store vault as a knowledge base",
  "settings.kb.details": "AI tools connected to Granite can search your store's notes.",

  "subs.intro": "Subscription plans set up in your store by your subscription app. Granite copies them into your notes; change them in the app that manages them.",
  "subs.openOverview": "Open overview in Granite",
  "subs.empty.heading": "No product in your store has subscription plans.",
  "subs.empty.body": "Plans made in Shopify Subscriptions or another subscription app appear here after the next sync.",
  "subs.group.app": "Managed by {app}",
  "subs.group.products": "Products",
  "subs.group.noProducts": "No products use this plan yet.",
  "subs.only.heading": "Only sold with a subscription",
  "subs.only.body": "Customers can buy these products only by choosing a subscription plan.",

  "granite.connect.heading": "Connect Granite",
  "granite.open.heading": "Open your store in Granite",
  "granite.open.body": "Your store's notes are in the {vault} vault. Read and edit them on the web, desktop or phone.",
  "granite.open.desktop": "Open in the desktop app",
  "granite.open.mobile": "Get the phone apps",
  "granite.qr.body": "Scan with your phone to open the vault in Granite.",
  "granite.qr.alt": "QR code that opens your store vault in Granite",
  "granite.account.heading": "Granite account",
  "granite.disconnect": "Disconnect",
  "granite.disconnect.heading": "Disconnect Granite?",
  "granite.disconnect.body": "Syncing stops. The {vault} vault and its notes stay in Granite.",
  "granite.disconnect.confirm": "Disconnect",
  "granite.disconnected": "Granite disconnected",
  "granite.kb.heading": "Knowledge base and AI tools",
  "granite.mcp.body": "Connect AI tools to Granite with this MCP address to search and read your store's notes.",
  "granite.mcp.label": "MCP server address",
  "granite.mcp.copy": "Copy",
  "granite.mcp.copied": "MCP address copied",
  "granite.mcp.copyFailed": "Couldn't copy. Select the address and copy it.",
  "granite.mcp.help": "How to connect AI tools",

  "usage.storageOf": "{used} of {limit} used",
  "usage.notes": "Notes by type",

  "activity.filter.attention": "Needs attention only",
  "activity.empty": "Nothing has happened yet.",
  "activity.empty.attention": "Nothing needs attention.",
  "activity.level.info": "Info",
  "activity.level.warning": "Warning",
  "activity.level.error": "Error",
  "activity.needsAttention": "Needs attention",
  "activity.resolvedBadge": "Resolved",
  "activity.openNote": "Open note",
  "activity.resolve": "Resolve",
  "activity.resolve.for": "Resolve: {message}",
  "activity.resolved": "Marked as resolved",

  "help.what.heading": "What the app does",
  "help.what.body": "Granite writes your products and store content as Markdown notes in a Granite vault and keeps them in sync.",
  "help.layout.heading": "Where your notes are",
  "help.layout.body": "Everything goes into one folder of the store vault, with a subfolder for each kind of content.",
  "help.notes.heading": "Editing your notes",
  "help.notes.body": "Add your own notes below the synced part of a note. Synced parts are rewritten when Shopify changes.",
  "help.sync.heading": "How syncing works",
  "help.sync.body": "Changes in Shopify reach your notes within minutes, and a daily check catches anything missed. Re-sync from the Catalog page.",
  "help.subscriptions.heading": "Subscriptions",
  "help.subscriptions.body": "Granite reads the subscription plans your subscription app set up and writes them into your product notes. It doesn't create plans or bill subscriptions; your subscription app does that.",
  "help.disconnect.heading": "Disconnecting and uninstalling",
  "help.disconnect.body": "Disconnecting or uninstalling stops syncing. Your vault and notes stay in Granite.",
  "help.readMore": "Read more: {topic}",
  "help.more": "The full help covers note formats, limits and subscription plans.",
  "help.all": "Open Granite for Shopify help",

  "count.separator": ", ",
  "count.products.one": "1 product",
  "count.products.other": "{count} products",
  "count.collections.one": "1 collection",
  "count.collections.other": "{count} collections",
  "count.pages.one": "1 page",
  "count.pages.other": "{count} pages",
  "count.blogs.one": "1 blog",
  "count.blogs.other": "{count} blogs",
  "count.articles.one": "1 blog post",
  "count.articles.other": "{count} blog posts",
  "count.policies.one": "1 policy",
  "count.policies.other": "{count} policies",
  "count.metaobjects.one": "1 metaobject",
  "count.metaobjects.other": "{count} metaobjects",
  "count.discounts.one": "1 discount",
  "count.discounts.other": "{count} discounts",
  "count.menus.one": "1 menu",
  "count.menus.other": "{count} menus",
  "count.files.one": "1 file",
  "count.files.other": "{count} files",
  "count.other": "{count} {type}",
} as const;

export type MessageKey = keyof typeof en;

export function isMessageKey(key: string): key is MessageKey {
  return Object.hasOwn(en, key);
}

/** Returns the UI string for key with {placeholders} filled in. */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const template: string = en[key];
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
}

/** Picks the singular or plural message for count (English rules), with {count} filled in. */
export function plural(count: number, one: MessageKey, other: MessageKey, vars?: Record<string, string | number>): string {
  const key = new Intl.PluralRules("en").select(count) === "one" ? one : other;
  return t(key, { count: count.toLocaleString("en"), ...vars });
}
